// src/App.js
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import BottomNavigation from './components/BottomNavigation';
import Recommendations from './pages/Recommendations';
import Auth from './components/auth/Auth';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Home from './pages/Home';
import Likes from './pages/Likes';
import Settings from './pages/Settings';
import Sale from './pages/Sale';
import Trending from './pages/Trending';
import ProductPage from './pages/ProductPage';
import Search from './pages/Search';
import OccasionProducts from './pages/OccasionProducts';
import Collection from './pages/Collection';

// Import Poppins font
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

// Brand Colors
const colors = {
  primary: {
    green: '#215F4B',
    greenDisabled: '#98aca4',
    lightGreen: '#DFFCC8',
    light: '#EFF4EF',
  },
  neutral: {
    black: '#1C1C1C',
    grey1: '#747474',
    grey2: '#A2A2A2',
    grey3: '#D9D9D9',
    grey4: '#F5F5F5',
    justInCase: '#D9D9D9',
  },
  danger: {
    main: '#D32F2F',
    light: '#FFEBEE'
  }
};

// Create theme with Poppins font and brand colors
const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary.green,
      disabled: colors.primary.greenDisabled,
      light: colors.primary.lightGreen,
      lighter: colors.primary.light,
      brandLight: '#EFF4EF',
      contrastText: '#fff',
      brandDark: '#cedece',
    },
    danger: {
      main: colors.danger.main,
      light: colors.danger.light,
    },
    text: {
      primary: colors.neutral.black,
      secondary: colors.neutral.grey1,
      disabled: colors.neutral.grey2,
    },
    background: {
      default: colors.primary.light,
      paper: '#fff',
    },
    divider: colors.neutral.grey4,
  },
  typography: {
    fontFamily: 'Poppins, sans-serif',
    h1: { fontWeight: 600, color: colors.neutral.black },
    h2: { fontWeight: 600, color: colors.neutral.black, fontSize: '35px' },
    h3: { fontWeight: 600, color: colors.neutral.black, fontSize: '32px' },
    h4: { fontWeight: 600, color: colors.neutral.black },
    h5: { fontWeight: 600, color: colors.neutral.black, fontSize: '16px' },
    h6: { fontWeight: 600, color: colors.neutral.black, fontSize: '15px' },
    subtitle1: { fontWeight: 500, color: colors.neutral.grey1 },
    subtitle2: { fontWeight: 500, color: colors.neutral.grey1 },
    body1: { fontWeight: 400, color: colors.neutral.black, fontSize: '15px' },
    body2: { fontWeight: 400, color: colors.neutral.grey1, fontSize: '15px' },
    body3: { fontWeight: 400, color: colors.neutral.grey1, fontSize: '13px' },
    body4: { fontWeight: 400, color: colors.neutral.grey1, fontSize: '12px' },
    button: { fontWeight: 500,  },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'Poppins, sans-serif',
          backgroundColor: 'transparent',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: colors.primary.green,
          '&:hover': {
            backgroundColor: colors.primary.green,
            opacity: 0.9,
          },
        },
        outlined: {
          borderColor: colors.primary.green,
          color: colors.primary.green,
          '&:hover': {
            borderColor: colors.primary.green,
            backgroundColor: colors.primary.lightGreen,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: 'none',
          border: `1px solid ${colors.neutral.grey3}`,
        },
      },
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/auth" />;
  }

  return children;
};

const AppContent = () => {
  const { user } = useAuth();

  return (
    <>
      {user && <Navbar />}
      <Routes>
        <Route path="/auth" element={
          user ? <Navigate to="/play" /> : <Auth />
        } />
        
        <Route path="/" element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } />
        <Route path="/play" element={
          <ProtectedRoute>
            <Recommendations />
          </ProtectedRoute>
        } />
        <Route path="/likes" element={
          <ProtectedRoute>
            <Likes />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/sale" element={
          <ProtectedRoute>
            <Sale />
          </ProtectedRoute>
        } />
        <Route path="/trending" element={
          <ProtectedRoute>
            <Trending />
          </ProtectedRoute>
        } />
        <Route path="/product/:id" element={
          <ProtectedRoute>
            <ProductPage />
          </ProtectedRoute>
        } />
        <Route path="/search" element={
          <ProtectedRoute>
            <Search />
          </ProtectedRoute>
        } />
        <Route path="/occasions/:occasion" element={<OccasionProducts />} />
        <Route path="/collection/:collectionId" element={
          <ProtectedRoute>
            <Collection />
          </ProtectedRoute>
        } />
      </Routes>
      {user && <BottomNavigation />}
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContent />
    </ThemeProvider>
  );
}

export default App;